import React from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Avatar,
  Fade,
  Chip,
} from '@mui/material';
import {
  Scale,
  CheckCircle,
} from '@mui/icons-material';
import { DESIGN_TOKENS } from '../../../design-system/tokens/colors';

export interface WeighingData {
  data: {
    success: boolean;
    details: string;
  };
  resumo: string;
  content_type: string;
  url: string;
}

export interface WeighingFeedbackProps {
  weighingData: WeighingData;
  onNavigate?: (url: string) => void;
}

const extractWeighingInfo = (resumoText: string) => {
  const match = resumoText.match(/Pesagem de (\d+(?:\.\d+)?)kg registrada para '([^']+)'/);
  if (match) {
    return {
      weight: match[1],
      animalName: match[2],
    };
  }
  return { weight: '0', animalName: 'Animal' };
};

const WeighingFeedback: React.FC<WeighingFeedbackProps> = ({ weighingData }) => {
  const { data, resumo, url } = weighingData;
  const { weight, animalName } = extractWeighingInfo(resumo);

  return (
    <Fade in timeout={800}>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          minHeight: '60vh',
          padding: 3,
          background: `linear-gradient(135deg, ${DESIGN_TOKENS.colors.primary[50]} 0%, ${DESIGN_TOKENS.colors.background.default} 100%)`,
        }}
      >
        {/* Ícone principal animado */}
        <Box
          sx={{
            position: 'relative',
            marginBottom: 3,
            animation: 'pulse 2s infinite',
            '@keyframes pulse': {
              '0%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}40`,
              },
              '70%': {
                transform: 'scale(1.05)',
                boxShadow: `0 0 0 20px ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
              '100%': {
                transform: 'scale(1)',
                boxShadow: `0 0 0 0 ${DESIGN_TOKENS.colors.primary[200]}00`,
              },
            },
          }}
        >
          <Avatar
            sx={{
              width: 80,
              height: 80,
              backgroundColor: DESIGN_TOKENS.colors.primary[100],
              border: `3px solid ${DESIGN_TOKENS.colors.primary[200]}`,
              boxShadow: `0 8px 32px ${DESIGN_TOKENS.colors.primary[200]}40`,
            }}
          >
            <Scale
              sx={{
                fontSize: 40,
                color: DESIGN_TOKENS.colors.primary[700],
              }}
            />
          </Avatar>
          
          {/* Ícone de sucesso sobreposto */}
          <Avatar
            sx={{
              width: 32,
              height: 32,
              backgroundColor: DESIGN_TOKENS.colors.primary[600],
              position: 'absolute',
              bottom: -4,
              right: -4,
              border: `2px solid ${DESIGN_TOKENS.colors.background.paper}`,
            }}
          >
            <CheckCircle
              sx={{
                fontSize: 20,
                color: DESIGN_TOKENS.colors.background.paper,
              }}
            />
          </Avatar>
        </Box>

        {/* Card principal */}
        <Card
          sx={{
            maxWidth: 400,
            width: '100%',
            borderRadius: 3,
            boxShadow: `0 12px 40px ${DESIGN_TOKENS.colors.primary[200]}30`,
            border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
            overflow: 'visible',
          }}
        >
          <CardContent sx={{ padding: 4, textAlign: 'center' }}>
            {/* Título principal */}
            <Typography
              variant="h5"
              sx={{
                fontWeight: 700,
                color: DESIGN_TOKENS.colors.text.primary,
                marginBottom: 1,
              }}
            >
              Pesagem Registrada!
            </Typography>

            {/* Subtítulo */}
            <Typography
              variant="body1"
              sx={{
                color: DESIGN_TOKENS.colors.text.secondary,
                marginBottom: 3,
              }}
            >
              O peso foi registrado com sucesso
            </Typography>

            {/* Informações da pesagem */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                gap: 2,
                marginBottom: 3,
                padding: 2,
                backgroundColor: DESIGN_TOKENS.colors.primary[50],
                borderRadius: 2,
                border: `1px solid ${DESIGN_TOKENS.colors.primary[100]}`,
              }}
            >
              {/* Nome do animal */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <img 
                  src="/vaca.svg" 
                  alt="Animal" 
                  style={{ 
                    width: 20, 
                    height: 20, 
                    filter: `brightness(0) saturate(100%) invert(20%) sepia(77%) saturate(1200%) hue-rotate(180deg) brightness(95%) contrast(101%)` 
                  }} 
                />
                <Typography
                  variant="h6"
                  sx={{
                    fontWeight: 600,
                    color: DESIGN_TOKENS.colors.primary[700],
                  }}
                >
                  {animalName}
                </Typography>
              </Box>

              {/* Peso registrado */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1 }}>
                <Chip
                  icon={<Scale sx={{ fontSize: 16 }} />}
                  label={`${weight} kg`}
                  sx={{
                    backgroundColor: DESIGN_TOKENS.colors.primary[700],
                    color: DESIGN_TOKENS.colors.background.paper,
                    fontWeight: 600,
                    fontSize: '1.1rem',
                    padding: '8px 4px',
                    '& .MuiChip-icon': {
                      color: DESIGN_TOKENS.colors.background.paper,
                    },
                  }}
                />
              </Box>
            </Box>

            {/* Mensagem de detalhes */}
            <Typography
              variant="body2"
              sx={{
                color: DESIGN_TOKENS.colors.text.tertiary,
                fontStyle: 'italic',
                marginBottom: 3,
              }}
            >
              {data.details}
            </Typography>
          </CardContent>
        </Card>
      </Box>
    </Fade>
  );
};

export default WeighingFeedback;
